import React from 'react';
import { Link } from 'react-router-dom';
import {
  SearchOutlined,
  LineChartOutlined,
  CodeOutlined,
  FileTextOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import useReveal from '../useReveal';
import './AIEMoat.css';

/**
 * AIEMoat — "The magic underneath": the chatbot-contrast beat + modality
 * chips + the cross-pillar links. Condensed in the Aug 5 revamp (mock
 * session): the old stat band is gone — the dated benchmark receipt now
 * lives in AIEProofSection, which directly follows this section.
 *
 * "Most AI employees are a chatbot pointed at one job. Ours is a deep-reasoning
 * super-agent that can do any job." — the category-defining copy for the
 * "AI employee vs chatbot" query cluster; keep it crawlable prose.
 *
 * Pillar cross-links are real <Link> anchors (not buttons) so crawlers can
 * follow them — this is the page's hub-to-pillar internal linking.
 */
const MODALITIES = [
  { Icon: SearchOutlined, label: 'Deep research' },
  { Icon: LineChartOutlined, label: 'Data analysis' },
  { Icon: CodeOutlined, label: 'Code' },
  { Icon: FileTextOutlined, label: 'Documents & decks' },
  { Icon: PictureOutlined, label: 'Images' },
  { Icon: VideoCameraOutlined, label: 'Video' },
  { Icon: DashboardOutlined, label: 'Dashboards' },
  { Icon: AppstoreOutlined, label: 'Apps' },
];

const AIEMoat = () => {
  const [ref, visible] = useReveal();

  return (
    <section className="aie-moat aie-section" ref={ref}>
      <div className={`aie-moat__inner aie-reveal ${visible ? 'is-visible' : ''}`}>
        <span className="aie-eyebrow">The magic underneath</span>
        <h2 className="aie-moat__title aie-display">
          Most &ldquo;AI employees&rdquo; are a chatbot pointed at one job.
          <br />
          <span className="aie-moat__title-accent">
            Ours is a super-agent that can do any job.
          </span>
        </h2>
        <p className="aie-moat__lead">
          A general employee is only as good as their ability to think. Background
          workers don&rsquo;t have a human watching every step, so deep
          reasoning becomes the core capability. Since October 2025 we&rsquo;ve been building
          that base: <strong>CellCog</strong>, a super-agent that&rsquo;s among
          the leaders in deep research and the most complete across every
          modality. The AI Employee <em>is</em> that super-agent, given a
          name, a memory, an inbox, and a shift.
        </p>

        <div className="aie-moat__modalities">
          {MODALITIES.map(({ Icon, label }) => (
            <div className="aie-moat__chip" key={label}>
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </div>
          ))}
        </div>

        <div className="aie-moat__pillars">
          <span className="aie-moat__pillars-label">Part of CellCog</span>
          <Link className="aie-moat__pillar" to="/super-agents">
            Super-Agents <ArrowRightOutlined />
          </Link>
          <Link className="aie-moat__pillar" to="/agent-to-agent">
            Agent-to-Agent <ArrowRightOutlined />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AIEMoat;
